import type { ProStudioContext } from '../xstate/prostudio.xstate.types';

/**
 * The id that scopes pype reads to the selected solution — the `start_chat_bot_id`
 * its channels' streams carry.
 *
 * The V6 pype list filters each pype's streams by `start_chat_bot_id` and then
 * drops pypes left with no streams (pype_controller.ex `index_with_streams_and_widgets`),
 * so passing this narrows the result to the pypes that actually back a channel of
 * this solution. Omitting it returns every pype on the customer.
 *
 * The route carries the solution's name, not its id, hence the lookup — a
 * solution is addressed by `name` or `displayName` depending on where the link
 * came from.
 *
 * Shape: `<customerName>.<solution uuid>`, mirroring the backend's
 * `getSolutionStartChatId` (agent-manager-v2, "Fixes for channel bot ids"), which
 * is what `createChannel` stamps on every new stream and what the backend's own
 * pype reads and solution deletion scope by. A bare uuid matched no stream, and
 * the solution would not load. The backend stamps this form on NEW channels of legacy
 * solutions too, but never rewrote their existing streams, so a legacy solution's
 * channels carry either id — read and match with {@link resolveBotSolutionIds}; this
 * single id is for writing a new one. `readLegacyBotSolutionId` answers Bot Manager
 * questions.
 *
 * `ctx.customerId` is the customer NAME (see `getProjectData`), not the uuid in
 * `projectProStudioSetting.customerId`. Without it there is no id to build, and
 * `undefined` is returned rather than a bare uuid that would silently match nothing.
 */
/**
 * The selected solution's uuid — what the backend's `createChannel` takes as `botId`: it
 * builds the stream's `<customerName>.<uuid>` start-chat id from it itself
 * (`getSolutionStartChatId`), so a pre-composed id would be prefixed a second time.
 */
export const resolveSolutionId = (
  ctx: Pick<ProStudioContext, 'solutions' | 'botName'>
): string | undefined => findSolutionByBotName(ctx)?.id ?? undefined;

export const resolveBotSolutionId = (
  ctx: Pick<ProStudioContext, 'solutions' | 'botName' | 'customerId'>
): string | undefined =>
  buildSolutionStartChatId(ctx.customerId, findSolutionByBotName(ctx)?.id);

/**
 * EVERY id the selected solution's streams may carry — the uuid form first (what any
 * channel created since the migration has), then, for a legacy solution, its
 * `botSolutionId` (what its pre-migration channels still have: the backend does not
 * rewrite existing streams). Reads and matches must accept any of these; a write of a
 * NEW id uses the first. Empty when there is no id to build.
 */
export const resolveBotSolutionIds = (
  ctx: Pick<ProStudioContext, 'solutions' | 'botName' | 'customerId'>
): string[] => solutionStartChatIds(ctx.customerId, findSolutionByBotName(ctx));

/** {@link resolveBotSolutionIds} for any solution, not only the selected one. */
export const solutionStartChatIds = (
  customerName: string | null | undefined,
  solution: { id?: string | null; botSolutionId?: string | null } | undefined
): string[] => {
  const ids = [
    buildSolutionStartChatId(customerName, solution?.id),
    solution?.botSolutionId || undefined,
  ].filter((id): id is string => Boolean(id));
  return [...new Set(ids)];
};

/**
 * The reverse of {@link solutionStartChatIds}: the solution a stream's
 * `start_chat_bot_id` belongs to, accepting either form it may take.
 */
export const findSolutionByStartChatId = <
  T extends { id?: string | null; botSolutionId?: string | null },
>(
  solutions: readonly T[] | null | undefined,
  customerName: string | null | undefined,
  startChatId: string | null | undefined
): T | undefined =>
  startChatId
    ? (solutions ?? []).find((solution) =>
        solutionStartChatIds(customerName, solution).includes(startChatId)
      )
    : undefined;

/**
 * `<customerName>.<solution uuid>` — the id a solution's channel streams carry, both as
 * `start_chat_bot_id` (the solution itself) and as `end_chat_bot_id` (a survey linked to
 * it). The one place this string is spelled; `undefined` when either part is missing.
 */
export const buildSolutionStartChatId = (
  customerName: string | null | undefined,
  solutionId: string | null | undefined
): string | undefined =>
  customerName && solutionId ? `${customerName}.${solutionId}` : undefined;

const findSolutionByBotName = ({
  solutions,
  botName,
}: Pick<ProStudioContext, 'solutions' | 'botName'>) => {
  if (!botName) {
    return undefined;
  }

  return solutions?.find(
    (candidate) =>
      candidate.name === botName || candidate.displayName === botName
  );
};

/**
 * The RAW `botSolutionId`, with no uuid fallback — i.e. present only for solutions that
 * predate the Agent Manager migration and therefore still have a Bot Manager counterpart.
 *
 * Use this to decide whether a Bot Manager call makes sense at all. The backend gates its
 * own Bot Manager work on exactly this (`if (solution.botSolutionId)` in
 * `delete-solution.ts`); calling those endpoints for a v2 solution just 404s.
 *
 * Deliberately separate from `resolveBotSolutionId`, which is defined for every solution
 * and so cannot answer "is this legacy?".
 */
export const readLegacyBotSolutionId = (
  ctx: Pick<ProStudioContext, 'solutions' | 'botName'>
): string | undefined => findSolutionByBotName(ctx)?.botSolutionId ?? undefined;
